/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { LoginAuthUserInputFields } from '@com-delta-idp/mnx-login-ui';
import { DashboardResponse } from '@com-delta-idp/mnx-oauth';
import {
  AnalyticsService as AdobeAnalyticsService,
  adobePageLoadAnalyticsMocks,
  adobePageLoadErrorAnalyticsMocks,
  adobeUserAnalyticsMocks,
  adobeUserLoggedInAnalyticsMocks,
  AnalyticsProfile,
  AnalyticsService,
  PageLoadAnalytics,
  PageLoadErrorAnalytics
} from '@com-delta-idp/mnx-utils';
import { loginAndGetDashboardResponse } from '../../models/login-authentication-screen.interface';

declare let idp: any;
declare let delta: any;

@Injectable({
  providedIn: 'root'
})
export class UpdateDataLayerService {
  constructor(
    private analyticsService: AnalyticsService,
    private adobeAnalyticsService: AdobeAnalyticsService
  ) {}

  updateErrorDetails(errorId: string, errorMessage: string | undefined): void {
    this.analyticsService.updateDataLayer<string>(
      'errorCode',
      errorId,
      'idp.dataLayer.digitalData.page.error'
    );

    this.analyticsService.updateDataLayer<string>(
      'errorMessage',
      errorMessage,
      'idp.dataLayer.digitalData.page.error'
    );
  }

  updateUserNameAsLoginType(
    userInputFields: LoginAuthUserInputFields | undefined
  ): void {
    const loginMethod = userInputFields?.lastName ? 'UserName' : 'Skymiles';
    this.analyticsService.updateDataLayer<string>(
      'loginMethod',
      loginMethod,
      'idp.dataLayer.digitalData.attributes'
    );
  }

  updateMedallionStatusAndSkyMilesNumber(
    smNumber: string | undefined,
    membershipLevel: string | undefined
  ): void {
    this.updateDataLayerProfileAttributes<string>('skymilesNumber', smNumber);
    this.updateDataLayerProfileAttributes<string>(
      'medallionStatus',
      membershipLevel
    );
  }

  triggerLoginDataLayerEvents(eventType: string) {
    this.analyticsService.dispatchCustomEvent(eventType, {});
  }

  setDeltaDataLayerProfileAttributes<T>(
    property: string,
    data: T | string | undefined
  ) {
    const dataObject =
      delta?.dataLayer?.digitalData?.user?.profile[0]?.attributes;
    if (dataObject) {
      dataObject[property] = data;
    }
  }

  setDdcfbDataForStandalonePage(
    response: Partial<DashboardResponse> | loginAndGetDashboardResponse
  ) {
    const dataObject = idp?.dataLayer?.digitalData?.user?.ddcfbData;
    if (dataObject) {
      dataObject['crprtAgrmntTypeCode'] = response['crprtAgrmntTypeCode'];
      dataObject['pgmType'] = response['pgmType'];
      dataObject['pgm'] = response['program'];
      dataObject['permissions'] = response['permType'];
    }
  }

  updateDataLayerProfileAttributes<T>(
    property: string,
    data: T | string | undefined
  ) {
    if (typeof idp !== 'undefined') {
      const dataObject = idp?.dataLayer?.digitalData?.user?.profile?.attributes;
      if (dataObject) {
        dataObject[property] = data;
      }
    } else if (typeof delta !== 'undefined') {
      this.setDeltaDataLayerProfileAttributes(property, data);
    } else {
      return;
    }
  }

  private getCommonAdobeDataLayerConfig(
    baseData: PageLoadAnalytics | PageLoadErrorAnalytics
  ): PageLoadAnalytics | PageLoadErrorAnalytics {
    const { title, URL } = window.document;
    return {
      ...baseData,
      page: {
        info: {
          ...baseData.page.info,
          page_name: title,
          page_url: URL,
          page_type: title
        }
      }
    };
  }

  private getUserProfileData(
    skymilesNumber = '',
    skymilesTier = '',
    amexCardType = '',
    availableMiles = '',
    email = ''
  ): AnalyticsProfile {
    return {
      skymiles_number: skymilesNumber,
      skymiles_tier: skymilesTier,
      amex_card_type: amexCardType,
      available_miles: availableMiles,
      email
    } as AnalyticsProfile;
  }

  updateAdobeDataLayerLoginSuccess(
    skymilesNumber = '',
    skymilesTier = '',
    amexCardType = '',
    availableMiles = '',
    email = ''
  ): void {
    let data = this.getCommonAdobeDataLayerConfig(
      adobePageLoadAnalyticsMocks as PageLoadAnalytics
    );
    data = {
      ...data,
      user: {
        ...data.user,
        attributes: {
          ...data.user.attributes,
          ...adobeUserLoggedInAnalyticsMocks.attributes
        },
        profile: this.getUserProfileData(
          skymilesNumber,
          skymilesTier,
          amexCardType,
          availableMiles,
          email
        )
      }
    };
    this.adobeAnalyticsService.setAnalytics(data);
  }

  updateAdobeDataLayerUnAuthenticated(): void {
    const data = {
      ...this.getCommonAdobeDataLayerConfig(
        adobePageLoadAnalyticsMocks as PageLoadAnalytics
      ),
      user: {
        ...adobePageLoadAnalyticsMocks.user,
        attributes: {
          ...adobePageLoadAnalyticsMocks.user.attributes,
          ...adobeUserAnalyticsMocks.attributes
        }
      }
    };
    this.adobeAnalyticsService.setAnalytics(data);
  }

  updateAdobeDataLayerError(
    skymilesNumber = '',
    skymilesTier = '',
    amexCardType = '',
    availableMiles = '',
    email = ''
  ): void {
    let data = this.getCommonAdobeDataLayerConfig(
      adobePageLoadErrorAnalyticsMocks as PageLoadErrorAnalytics
    );
    data = {
      ...data,
      user: {
        ...data.user,
        attributes: {
          ...data.user.attributes,
          ...adobeUserAnalyticsMocks.attributes
        },
        profile: this.getUserProfileData(
          skymilesNumber,
          skymilesTier,
          amexCardType,
          availableMiles,
          email
        )
      }
    };
    this.adobeAnalyticsService.setAnalytics(data);
  }
}
